import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';

import { getView } from '../../engine/prism.js';
import { partAt, resolvePartPath, setOps, type ModelOp } from '../../lib/model-doc.js';
import { useCommands, useKeyHint, useShortcutContext, suspendShortcuts, type CommandSpec } from '../../lib/shortcuts/index.js';
import { logEditor } from '../../state/console.js';
import { useModel } from '../../state/model.js';
import { Card, IconButton, NumberField, PropRow, SelectField, Toggle } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import {
  buildDecimateOp,
  buildModelOp,
  buildOp,
  buildRemeshOp,
  DEFAULT_REMESH_STATE,
  DELIMIT_KINDS,
  formatVoxelSize,
  GESTURE_FALLBACK_SIZE,
  kindOf,
  onRemeshStats,
  previewParams,
  remeshReducer,
  statsLine,
  TARGET_FACES_MAX,
  TARGET_FACES_MIN,
  UNSUBDIVIDE_MAX,
  voxelSizeFromDrag,
  VOXEL_MAX,
  type DecimateMode,
  type RemeshAction,
  type RemeshMode,
  type RemeshPanelState,
  type RemeshStats,
  type RemeshTab,
  type SymmetryAxis,
} from './remesh-ops.js';

/**
 * Remesh & Decimate card of the Part inspector (Blender's Remesh / Decimate
 * modifiers and sculpt-mode voxel remesh): voxel or quad remesh, collapse /
 * planar / un-subdivide decimation, a live "current → result" triangle count
 * from the engine's `remeshPreview`, "Add as modifier" (a `remesh` / `decimate`
 * op, non-destructive) and "Apply" (bakes the part into the result mesh).
 *
 * Shortcuts: Ctrl+R voxel remesh and Shift+R voxel-size drag while sculpting
 * (`model.sculpt`), Ctrl+Alt+R quad remesh with the panel's settings (`model`).
 */

/** Debounce of the live preview while parameters change. */
const PREVIEW_DELAY_MS = 250;
/** Faster while dragging the voxel size (the drag is the only input). */
const GESTURE_PREVIEW_DELAY_MS = 150;

interface RemeshStore {
  state: RemeshPanelState;
  /** Shift+R voxel-size drag in progress. */
  gesture: { path: string; start: number } | null;
  dispatch: (action: RemeshAction) => void;
}

/** Panel settings, kept while parts are selected / deselected (shortcuts read them too). */
export const useRemesh = create<RemeshStore>((set) => ({
  state: DEFAULT_REMESH_STATE,
  gesture: null,
  dispatch: (action) => set((s) => ({ state: remeshReducer(s.state, action) })),
}));

/** Part the shortcuts act on: the sculpted part while sculpting, else the selection. */
function targetPath(): string | null {
  const s = useModel.getState();
  return (s.sculpt.active ? s.sculpt.path : null) ?? s.selected;
}

/** Sends a destructive remesh / decimate; the engine answers with `modelChanged` (undo + save). */
function applyOp(op: ModelOp, path: string | null = targetPath()): void {
  if (!path) return;
  const view = getView('model');
  if (!view) {
    logEditor('warn', 'Remesh needs the engine viewport (run pnpm engine:build).');
    return;
  }
  void view.modelCommand(buildModelOp(useRemesh.getState().state, path, op)).then((outcome) => {
    if (!outcome.ok) logEditor('warn', `${op.op}: ${outcome.error ?? 'failed'}`);
  });
}

function applyRemesh(mode: RemeshMode, path?: string | null): void {
  applyOp(buildRemeshOp(useRemesh.getState().state, mode), path);
}

function startVoxelGesture(): void {
  const path = targetPath();
  if (!path || useRemesh.getState().gesture) return;
  useRemesh.setState({ gesture: { path, start: useRemesh.getState().state.voxel.voxelSize } });
}

const hasTarget = () => Boolean(useModel.getState().doc && targetPath());

const REMESH_COMMANDS: readonly CommandSpec[] = [
  {
    id: 'model.sculpt.voxelRemesh',
    label: 'Voxel remesh',
    category: 'Sculpt',
    context: 'model.sculpt',
    keys: ['Ctrl+R'],
    icon: 'grid',
    keywords: 'remesh voxel retopology rebuild topology',
    enabled: hasTarget,
    run: () => applyRemesh('voxel'),
  },
  {
    id: 'model.sculpt.voxelSize',
    label: 'Voxel size',
    category: 'Sculpt',
    context: 'model.sculpt',
    keys: ['Shift+R'],
    icon: 'grid',
    keywords: 'remesh voxel resolution detail drag',
    enabled: hasTarget,
    run: startVoxelGesture,
  },
  {
    id: 'model.remesh.quad',
    label: 'Quad remesh',
    category: 'Model',
    context: 'model',
    keys: ['Ctrl+Alt+R'],
    icon: 'grid',
    keywords: 'remesh quads retopology quadriflow',
    enabled: hasTarget,
    run: () => applyRemesh('quad'),
  },
  {
    id: 'model.remesh.decimate',
    label: 'Decimate',
    category: 'Model',
    context: 'model',
    icon: 'grid',
    keywords: 'reduce polygons simplify lod collapse planar unsubdivide',
    enabled: hasTarget,
    run: () => {
      const state = useRemesh.getState().state;
      applyOp(buildDecimateOp(state));
    },
  },
];

/** Last pointer position over the window: where the Shift+R HUD appears. */
let lastPointer: { x: number; y: number } | null = null;

/** The Remesh & Decimate card (mounted by the Part inspector). */
export function RemeshPanel() {
  const selected = useModel((s) => s.selected);
  const sculpting = useModel((s) => s.sculpt.active);
  const gesture = useRemesh((s) => s.gesture);
  const dispatch = useRemesh((s) => s.dispatch);

  useShortcutContext('model.sculpt', sculpting);
  useCommands(REMESH_COMMANDS, []);
  useEffect(() => {
    const track = (e: PointerEvent) => {
      lastPointer = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', track, { passive: true });
    return () => {
      window.removeEventListener('pointermove', track);
      useRemesh.setState({ gesture: null });
    };
  }, []);

  if (!selected) return null;
  return (
    <>
      <Card
        title="Remesh & Decimate"
        icon="grid"
        defaultOpen={false}
        actions={<IconButton icon="refresh" title="Reset parameters" size={13} onClick={() => dispatch({ type: 'reset' })} />}
      >
        <RemeshBody path={selected} />
      </Card>
      {gesture && createPortal(<VoxelSizeHud key={gesture.path} path={gesture.path} start={gesture.start} />, document.body)}
    </>
  );
}

const TABS: readonly [RemeshTab, string, string][] = [
  ['voxel', 'Voxel', 'Rebuild as an even voxel surface (watertight, uniform detail)'],
  ['quad', 'Quad', 'Rebuild with flowing quads (retopology)'],
  ['decimate', 'Decimate', 'Reduce the polygon count'],
];

const SYMMETRY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'x', label: 'X' },
  { value: 'y', label: 'Y' },
  { value: 'z', label: 'Z' },
];

const DECIMATE_OPTIONS: { value: DecimateMode; label: string }[] = [
  { value: 'collapse', label: 'Collapse' },
  { value: 'planar', label: 'Planar' },
  { value: 'unsubdivide', label: 'Un-subdivide' },
];

const DELIMIT_LABELS: Record<string, string> = { normal: 'Normal', material: 'Material', seam: 'Seam', sharp: 'Sharp', uv: 'UV' };

const symmetryValue = (axis: SymmetryAxis | null) => axis ?? 'none';
const symmetryOf = (value: string): SymmetryAxis | null => (value === 'x' || value === 'y' || value === 'z' ? value : null);

function RemeshBody({ path }: { path: string }) {
  const state = useRemesh((s) => s.state);
  const dispatch = useRemesh((s) => s.dispatch);
  const doc = useModel((s) => s.doc);
  const update = useModel((s) => s.update);
  const sizeKey = useKeyHint('model.sculpt.voxelSize');
  const remeshKey = useKeyHint(state.tab === 'quad' ? 'model.remesh.quad' : 'model.sculpt.voxelRemesh');

  const [stats, setStats] = useState<RemeshStats | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A preview was sent and its `remeshStats` has not arrived (older answers are ignored). */
  const outstanding = useRef(false);
  const kind = kindOf(state);
  const request = JSON.stringify(previewParams(state, path));

  useEffect(() => {
    setStats(null);
    return onRemeshStats((s) => {
      if (s.path !== path || s.kind !== kind || !outstanding.current) return;
      outstanding.current = false;
      setStats(s);
      setPending(false);
    });
  }, [path, kind]);

  useEffect(() => {
    outstanding.current = false;
    setPending(true);
    setError(null);
    const timer = setTimeout(() => {
      const view = getView('model');
      if (!view) {
        setPending(false);
        setError('Preview needs the engine viewport.');
        return;
      }
      outstanding.current = true;
      void view.modelCommand(JSON.parse(request) as object).then((outcome) => {
        if (!outcome.ok) {
          outstanding.current = false;
          setPending(false);
          setError(outcome.error ?? 'Preview failed.');
        }
      });
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
    // `doc`: the part may have changed (edits, undo, a previous Apply).
  }, [request, doc]);

  const addModifier = () => {
    if (!doc) return;
    const index = resolvePartPath(doc, path);
    const part = index ? partAt(doc, index) : null;
    if (!index || !part) return;
    update(setOps(doc, index, [...(part.ops ?? []), buildOp(state)]));
  };

  const voxel = (patch: Partial<RemeshPanelState['voxel']>) => dispatch({ type: 'voxel', patch });
  const quad = (patch: Partial<RemeshPanelState['quad']>) => dispatch({ type: 'quad', patch });
  const decimate = (patch: Partial<RemeshPanelState['decimate']>) => dispatch({ type: 'decimate', patch });
  const v = state.voxel;
  const q = state.quad;
  const d = state.decimate;

  return (
    <>
      <div className="subtabs" role="tablist" aria-label="Remesh mode" style={{ height: 28, margin: '0 0 6px', padding: 0 }}>
        {TABS.map(([tab, text, hint]) => (
          <button
            key={tab}
            type="button"
            role="tab"
            title={hint}
            aria-selected={state.tab === tab}
            className={`subtab${state.tab === tab ? ' is-active' : ''}`}
            onClick={() => dispatch({ type: 'tab', tab })}
          >
            {text}
          </button>
        ))}
      </div>

      {state.tab === 'voxel' && (
        <>
          <PropRow label="Voxel size" hint={`meters; 0 = auto (longest side / 64)${sizeKey ? ` — ${sizeKey} drags it while sculpting` : ''}`}>
            <NumberField value={v.voxelSize} step={0.005} min={0} max={VOXEL_MAX} onChange={(x) => voxel({ voxelSize: x })} />
          </PropRow>
          <PropRow label="Adaptivity" hint="0..1: larger faces where the surface is flat">
            <NumberField value={v.adaptivity} step={0.05} min={0} max={1} onChange={(x) => voxel({ adaptivity: x })} />
          </PropRow>
          <PropRow label="Fix poles" hint="reduce vertices where many edges meet">
            <Toggle checked={v.fixPoles} onChange={(x) => voxel({ fixPoles: x })} />
          </PropRow>
          <PropRow label="Smooth normals">
            <Toggle checked={v.smoothNormals} onChange={(x) => voxel({ smoothNormals: x })} />
          </PropRow>
          <PropRow label="Preserve volume" hint="keep the enclosed volume after remeshing">
            <Toggle checked={v.preserveVolume} onChange={(x) => voxel({ preserveVolume: x })} />
          </PropRow>
          <PropRow label="Project colors" hint="carry vertex colors over to the new mesh">
            <Toggle checked={v.projectColors} onChange={(x) => voxel({ projectColors: x })} />
          </PropRow>
          <PropRow label="Project UVs" hint="carry UVs over to the new mesh">
            <Toggle checked={v.projectUvs} onChange={(x) => voxel({ projectUvs: x })} />
          </PropRow>
        </>
      )}

      {state.tab === 'quad' && (
        <>
          <PropRow label="Target faces">
            <NumberField value={q.targetFaces} integer step={100} min={TARGET_FACES_MIN} max={TARGET_FACES_MAX} onChange={(x) => quad({ targetFaces: x })} />
          </PropRow>
          <PropRow label="Adaptive size" hint="0..1: smaller quads where curvature is high">
            <NumberField value={q.adaptiveSize} step={0.05} min={0} max={1} onChange={(x) => quad({ adaptiveSize: x })} />
          </PropRow>
          <PropRow label="Symmetry">
            <SelectField value={symmetryValue(q.symmetry)} options={SYMMETRY_OPTIONS} onChange={(x) => quad({ symmetry: symmetryOf(x) })} />
          </PropRow>
          <PropRow label="Preserve sharp" hint="follow edges sharper than the angle">
            <Toggle checked={q.preserveSharp} onChange={(x) => quad({ preserveSharp: x })} />
          </PropRow>
          {q.preserveSharp && (
            <PropRow label="Sharp angle" hint="degrees">
              <NumberField value={q.sharpAngle} step={1} min={0} max={180} onChange={(x) => quad({ sharpAngle: x })} />
            </PropRow>
          )}
          <PropRow label="Preserve boundary" hint="keep open borders in place">
            <Toggle checked={q.preserveBoundary} onChange={(x) => quad({ preserveBoundary: x })} />
          </PropRow>
          <PropRow label="Smoothing" hint="relax iterations">
            <NumberField value={q.smoothing} integer step={1} min={0} max={100} onChange={(x) => quad({ smoothing: x })} />
          </PropRow>
        </>
      )}

      {state.tab === 'decimate' && (
        <>
          <PropRow label="Method">
            <SelectField
              value={state.decimateMode}
              options={DECIMATE_OPTIONS}
              onChange={(x) => dispatch({ type: 'decimateMode', mode: x as DecimateMode })}
            />
          </PropRow>
          {state.decimateMode === 'collapse' && (
            <>
              <PropRow label="Ratio" hint="share of triangles to keep">
                <NumberField value={d.ratio} step={0.05} min={0} max={1} onChange={(x) => decimate({ ratio: x })} />
              </PropRow>
              <PropRow label="Target triangles" hint="overrides the ratio when > 0">
                <NumberField value={d.targetTriangles} integer step={100} min={0} onChange={(x) => decimate({ targetTriangles: x })} />
              </PropRow>
              <PropRow label="Preserve boundary">
                <Toggle checked={d.preserveBoundary} onChange={(x) => decimate({ preserveBoundary: x })} />
              </PropRow>
              <PropRow label="Preserve seams" hint="keep UV seams">
                <Toggle checked={d.preserveSeams} onChange={(x) => decimate({ preserveSeams: x })} />
              </PropRow>
              <PropRow label="Preserve sharp">
                <Toggle checked={d.preserveSharp} onChange={(x) => decimate({ preserveSharp: x })} />
              </PropRow>
              {d.preserveSharp && (
                <PropRow label="Sharp angle" hint="degrees">
                  <NumberField value={d.sharpAngle} step={1} min={0} max={180} onChange={(x) => decimate({ sharpAngle: x })} />
                </PropRow>
              )}
              <PropRow label="Symmetry">
                <SelectField value={symmetryValue(d.symmetry)} options={SYMMETRY_OPTIONS} onChange={(x) => decimate({ symmetry: symmetryOf(x) })} />
              </PropRow>
              <PropRow label="Triangulate" hint="output triangles only">
                <Toggle checked={d.triangulate} onChange={(x) => decimate({ triangulate: x })} />
              </PropRow>
            </>
          )}
          {state.decimateMode === 'planar' && (
            <>
              <PropRow label="Angle limit" hint="degrees: merge faces flatter than this">
                <NumberField value={d.angleLimit} step={1} min={0} max={180} onChange={(x) => decimate({ angleLimit: x })} />
              </PropRow>
              {DELIMIT_KINDS.map((k) => (
                <PropRow key={k} label={`Delimit ${DELIMIT_LABELS[k] ?? k}`} hint="never dissolve across these">
                  <Toggle
                    checked={d.delimit.includes(k)}
                    onChange={(on) => decimate({ delimit: on ? [...d.delimit, k] : d.delimit.filter((x) => x !== k) })}
                  />
                </PropRow>
              ))}
              <PropRow label="All boundaries" hint="dissolve boundary vertices too">
                <Toggle checked={d.allBoundaries} onChange={(x) => decimate({ allBoundaries: x })} />
              </PropRow>
            </>
          )}
          {state.decimateMode === 'unsubdivide' && (
            <PropRow label="Iterations" hint="subdivision levels to undo">
              <NumberField value={d.iterations} integer step={1} min={1} max={UNSUBDIVIDE_MAX} onChange={(x) => decimate({ iterations: x })} />
            </PropRow>
          )}
        </>
      )}

      <p className="muted card-note mono" title={stats ? `computed in ${Math.round(stats.ms)} ms` : undefined}>
        {statsLine(stats, pending, state.tab === 'quad')}
      </p>
      {error && <p className="card-note jsonfield-error">{error}</p>}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button type="button" className="tb-btn" title="Append to the part's operations (non-destructive, editable later)" onClick={addModifier}>
          <Icon name="plus" size={13} /> Add as modifier
        </button>
        <button
          type="button"
          className="tb-btn"
          title={`Replace the part with the result mesh (undoable)${remeshKey && state.tab !== 'decimate' ? ` (${remeshKey})` : ''}`}
          onClick={() => applyOp(buildOp(state), path)}
        >
          <Icon name="check" size={13} /> Apply
        </button>
      </div>
    </>
  );
}

/**
 * Shift+R: drag left / right to pick the voxel size (log scale) with a live
 * triangle count; LMB / Enter remeshes, RMB / Esc cancels. Modal: keys and
 * clicks go nowhere else until it ends.
 */
function VoxelSizeHud({ path, start }: { path: string; start: number }) {
  const base = start > 0 ? start : GESTURE_FALLBACK_SIZE;
  const [size, setSize] = useState(base);
  const [pos, setPos] = useState(lastPointer);
  const [stats, setStats] = useState<RemeshStats | null>(null);
  const [pending, setPending] = useState(true);
  const sizeRef = useRef(base);
  const outstanding = useRef(false);

  useEffect(() => {
    let originX = lastPointer?.x ?? null;
    const resume = suspendShortcuts();
    const cursor = document.body.style.cursor;
    document.body.style.cursor = 'ew-resize';
    let done = false;
    const finish = (confirm: boolean) => {
      if (done) return;
      done = true;
      useRemesh.setState({ gesture: null });
      if (!confirm) return;
      useRemesh.getState().dispatch({ type: 'voxel', patch: { voxelSize: sizeRef.current } });
      applyRemesh('voxel', path);
    };
    const swallow = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    const move = (e: PointerEvent) => {
      if (originX === null) originX = e.clientX;
      const next = voxelSizeFromDrag(base, e.clientX - originX);
      sizeRef.current = next;
      setSize(next);
      setPos({ x: e.clientX, y: e.clientY });
    };
    const down = (e: PointerEvent) => {
      swallow(e);
      if (e.button === 0) finish(true);
      else if (e.button === 2) finish(false);
    };
    const key = (e: KeyboardEvent) => {
      swallow(e);
      if (e.key === 'Enter' || e.key === ' ') finish(true);
      else if (e.key === 'Escape') finish(false);
    };
    const unsubscribe = onRemeshStats((s) => {
      if (s.path !== path || s.kind !== 'remesh' || !outstanding.current) return;
      outstanding.current = false;
      setStats(s);
      setPending(false);
    });
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('keydown', key, true);
    window.addEventListener('contextmenu', swallow, true);
    return () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('contextmenu', swallow, true);
      unsubscribe();
      document.body.style.cursor = cursor;
      resume();
    };
  }, [path, base]);

  useEffect(() => {
    outstanding.current = false;
    setPending(true);
    const timer = setTimeout(() => {
      const state = remeshReducer(useRemesh.getState().state, { type: 'voxel', patch: { voxelSize: size } });
      outstanding.current = true;
      const view = getView('model');
      const failed = () => {
        outstanding.current = false;
        setPending(false);
      };
      if (!view) failed();
      else
        void view.modelCommand(previewParams(state, path, buildRemeshOp(state, 'voxel'))).then((outcome) => {
          if (!outcome.ok) failed();
        });
    }, GESTURE_PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [size, path]);

  const x = (pos?.x ?? window.innerWidth / 2) + 18;
  const y = (pos?.y ?? window.innerHeight / 2) + 18;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: Math.min(x, window.innerWidth - 230),
        top: Math.min(y, window.innerHeight - 80),
        zIndex: 10000,
        pointerEvents: 'none',
        padding: '6px 10px',
        background: 'var(--panel-2)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
        fontSize: 12,
        lineHeight: 1.5,
        color: 'var(--text)',
      }}
    >
      <div>
        Voxel size <strong className="mono">{formatVoxelSize(size)}</strong>
      </div>
      <div className="mono muted">{statsLine(stats, pending)}</div>
      <div className="muted" style={{ fontSize: 11 }}>
        Drag ←→ · Click / Enter remesh · Right-click / Esc cancel
      </div>
    </div>
  );
}
